import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { SearchBox } from "../../../components/Search";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart } from "../../../models/chart_model";
import { ChhattisgarhDCNetScheduleItem } from "../../../models/rajasthan";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { H_CHART_TYPES } from "../../Hppc/const";
import { formatDateForAPI } from "../const";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import {
  getDcNetshcdDayAheadDc,
  getDcNetshcdDayAheadDcMetrics,
} from "../Utility/RestApi";
import {
  DcNetshcdDayAheadDcMetrics
} from "../../../models/metrics";
import { normalizeDisplayLabel } from "../../../utility/displayText";
let Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];
const UNITS = ["MW", "MWh"];
const TYPE_OPTIONS_BASE = [
  { label: "Day Ahead DC", suffixBase: "dayahead_dc", displayName: "Day Ahead DC" },
  { label: "DC", suffixBase: "dc", displayName: "DC" },
  { label: "Schedule", suffixBase: "schedule", displayName: "Schedule" },
  { label: "DC Deviation", suffixBase: "dc_minus_dayahead_dc", displayName: "DC Deviation" },
  { label: "DC Schedule Deviation", suffixBase: "dc_minus_schedule", displayName: "DC Schedule Deviation" },
];

// Helper function to get month with year (e.g., "Oct-24" instead of just "Oct")
export function getMonthWithYear(dateString: string): string {
  const date = new Date(dateString);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
}

export function ChhattisgarhDCNetSchedule() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [data, setData] = useState<ChhattisgarhDCNetScheduleItem[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [plantNames, setPlantNames] = useState<string[]>([]);
  const [selectedPlants, setSelectedPlants] = useState<string[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(["Day Ahead DC", "DC", "Schedule", "DC Deviation", "DC Schedule Deviation"]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] =
    useState<DcNetshcdDayAheadDcMetrics | null>(null);

  const TYPE_OPTIONS = useMemo(
    () =>
      TYPE_OPTIONS_BASE.map((t) => ({
        ...t,
        suffix: `${t.suffixBase}_${selectedUnit.toLowerCase()}`,
      })),
    [selectedUnit],
  );

  const selectedTypeSuffixes = useMemo(() => {
    return TYPE_OPTIONS.filter((t) => selectedTypes.includes(t.label));
  }, [selectedTypes, TYPE_OPTIONS]);

  const availablePlantNames = useMemo(() => plantNames, [plantNames]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);
    try {
      const data = await getDcNetshcdDayAheadDc(
        startDateStr,
        endDateStr,
      );
      const dataMap = new Map<string, ChhattisgarhDCNetScheduleItem>();
      data.forEach((item) => {
        const uniqueKey = `${item.date}_${item.block}_${item.entityid}`;
        if (!dataMap.has(uniqueKey)) {
          dataMap.set(uniqueKey, item);
        }
      });
      const newData = Array.from(dataMap.values());

      setData(newData);

      const uniquePlants = Array.from(
        new Set(newData.map((item) => item.entityid)),
      ).sort();
      setPlantNames(uniquePlants);

      setUniqueDate(
        Array.from(
          new Set(
            newData
              .map((item) => item.date)
              .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
          ),
        ),
      );
      setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  const _handlefetchMetrics = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);

    const blocks = selectedBlock.length > 0 ? selectedBlock.map((b) => b.value) : undefined;
    const entityid = selectedPlants.length === 1 ? selectedPlants[0] : undefined;
    const ranges = [{ startDate: startDateStr, endDate: endDateStr, ...(blocks ? { blocks } : {}) }];

    try {
      const res = await getDcNetshcdDayAheadDcMetrics({ ranges, entityid });
      setMetrics(res);
    } catch (error) {
      console.error("Error in metrics fetching:", error);
    }
  }, [DefaultDates, activeTab, selectedBlock, selectedPlants]);

  useEffect(() => {
    let filteredData: any[] = data.map((item) => ({
      ...item,
      month: getMonthWithYear(item.date),
    }));

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedBlock.map((b) => b.value).includes(item.block),
      );
    }
    if (selectedPlants.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedPlants.includes(item.entityid),
      );
    }

    // Compute MWh fields (each block = 15 min = 0.25 hr)
    filteredData = filteredData.map((item: any) => ({
      ...item,
      dc_mwh: (item.dc_mw ?? 0) * 0.25,
      schedule_mwh: (item.schedule_mw ?? 0) * 0.25,
      dayahead_dc_mwh: item.dayahead_dc_mw != null ? item.dayahead_dc_mw * 0.25 : null,
      dc_minus_schedule_mwh: item.dc_minus_schedule != null ? item.dc_minus_schedule * 0.25 : null,
      dc_minus_dayahead_dc_mwh: item.dc_minus_dayahead_dc != null ? item.dc_minus_dayahead_dc * 0.25 : null,
    }));

    // Sort filtered data in ascending order by date and block
    filteredData.sort((a: any, b: any) => {
      const dateCompare =
        new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.block - b.block;
    });

    function getKeys() {
      if (selectedInterval.key === "slot") {
        return ["date", "block"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      } else {
        return [selectedInterval.values[0]];
      }
    }

    // Get the aggregation keys based on selected types
    const aggKeys = selectedTypeSuffixes.map((t) => t.suffix);

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: ["entityid"],
      tableKeys: ["date", "block", "month"],
      volumeKey: ``,
      aggregationKeys: aggKeys,
      priceKey: "",
      averageKeys: aggKeys,
      averageDuplicationKeys: ["date", "block", "month", "entityid"],
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    data,
    selectedInterval,
    selectedPlants,
    selectedDate,
    selectedBlock,
    selectedTypeSuffixes,
    selectedUnit,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    _handlefetchMetrics();
  }, [_handlefetchMetrics]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.isTime) {
          return "block";
        } else {
          return selectedInterval.values[0];
        }
      }
      return "block";
    } else {
      return selectedInterval.key;
    }
  }, [
    selectedBlock.length,
    selectedInterval.isTime,
    selectedInterval.key,
    selectedInterval.values,
  ]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  const summaryExtraKeys = selectedInterval.isTime
    ? ["block", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value,
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(getLegends());

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    block_key: "block",
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "CSPDCL DC Net Schedule",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(Intervals[index]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => setSelectedUnit(item)}
          />
          <FilterMenu
            title="Type"
            items={TYPE_OPTIONS.map((t) => t.label)}
            selectedItems={selectedTypes}
            setSelectedItems={setSelectedTypes}
            sortAlphabetically={false}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              items={[{ label: "Avg Declared Capacity", value: metrics["Avg Declared Capacity (MW)"]?.value, unit: "MW" }]}
            />
            <MetricChip
              items={[{ label: "Avg Schedule", value: metrics["Avg Schedule (MW)"]?.value, unit: "MW" }]}
            />
            <MetricChip
              items={[{ label: "Avg Surplus Capacity", value: metrics["Avg Surplus Capacity (MW)"]?.value, unit: "MW" }]}
            />
            <MetricChip
              items={[{ label: "Avg Day Ahead DC Deviation", value: metrics["Avg Day-Ahead DC Deviation (MW)"]?.value, unit: "MW" }]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">DC vs Actual ({selectedUnit})</h2>
            <div className="flex gap-x-6  justify-end">
              <SearchBox
                options={availablePlantNames.map((p) => ({
                  label: p,
                  value: p,
                }))}
                selectedOptions={selectedPlants.map((p) => ({
                  label: p,
                  value: p,
                }))}
                onChange={(selected) => {
                  setSelectedPlants(
                    (selected || []).map((item: any) => item.value),
                  );
                }}
                placeholder="Search Entity"
                isMany={true}
              />
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {loading ? (
            <ChartAreaLoader
              title="Loading DC Net Schedule"
              subtitle="Fetching CSPDCL DC Net Schedule data…"
            />
          ) : (
            <>
              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 30 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={getLegends()}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="
            1. Actual Load data is sourced from CSPDCL's official records."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );

  function getLegends() {
    // For each selected type suffix (dc_mw, net_schedule_mw, deviation_mw),
    // create a legend entry for each plant (chartKeys = plant names from groupBy)
    return chartKeys.flatMap((key) =>
      selectedTypeSuffixes.map(({ suffix, displayName }) => ({
        dataKey: `${key}_${suffix}`,
        name: `(${normalizeDisplayLabel(key)}) ${displayName}`,
        legendColor: undefined,
        type: selectedChartType.value,
        stackId:
          (selectedChartType as any).stackId !== undefined ? suffix : undefined,
      })),
    );
  }
}
